import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import { useLocalStorage, useResizableSidePanel } from '@sketch/utils'
import { localStorageKeys } from '@sketch/constants'
import { useCanvasInteractions } from '../CanvasInteractions'

const DEFAULT_LEFT_PANEL_WIDTH = 230

interface canvasLeftPanelResizingContext {
  isResizing: boolean
  width: number
  resizeHandleRef: (node: HTMLElement | null) => void
}

const canvasLeftPanelResizingContext =
  createContext<canvasLeftPanelResizingContext>({
    isResizing: false,
    width: 230,
    resizeHandleRef: () => {},
  })

/**
 * Provider to enable resizing of the canvas left panel and share its state
 * with other parts of the app (canvas inspector needs to know if we are
 * resizing to disable interactions).
 */
export const CanvasLeftPanelResizingProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const { localStorageWidth, setLocalStorageWidth } =
    useSaveWidthToLocalStorage()
  const { setEnabled } = useCanvasInteractions()

  const { width, isResizing, resizeHandleRef } = useResizableSidePanel({
    initialWidth: localStorageWidth,
    defaultWidth: DEFAULT_LEFT_PANEL_WIDTH,
    minWidth: 230,
    maxWidth: 400,
    onWidthUpdate: setLocalStorageWidth,
  })

  // Disable canvas interactions when resizing
  useEffect(() => {
    setEnabled(!isResizing)
  }, [isResizing, setEnabled])

  const value = useMemo(
    () => ({ isResizing, width, resizeHandleRef }),
    [isResizing, width, resizeHandleRef]
  )

  return (
    <canvasLeftPanelResizingContext.Provider value={value}>
      {children}
    </canvasLeftPanelResizingContext.Provider>
  )
}

export function useCanvasLeftPanelResizing() {
  const context = useContext(canvasLeftPanelResizingContext)
  if (!context) {
    throw new Error(
      'useCanvasLeftPanelResizing must be used within a CanvasLeftPanelResizingProvider'
    )
  }

  return context
}

function useSaveWidthToLocalStorage() {
  const [storedWidth, setStoredWidth, removeValue] = useLocalStorage(
    localStorageKeys.canvasLeftPanelWidth,
    DEFAULT_LEFT_PANEL_WIDTH
  )

  // Use a ref to access it in the saveResizeWidth callback
  // and avoid recreating the callback on each width change
  const storedWidthRef = React.useRef(storedWidth)
  storedWidthRef.current = storedWidth

  const saveResizeWidth = useCallback(
    (width: number) => {
      if (width === DEFAULT_LEFT_PANEL_WIDTH) {
        removeValue()
        return
      }
      if (width === storedWidthRef.current) {
        return
      }

      setStoredWidth(width)
    },
    [removeValue, setStoredWidth, storedWidthRef]
  )

  return {
    localStorageWidth: storedWidth,
    setLocalStorageWidth: saveResizeWidth,
  }
}
